import i18n from '@/i18n'
const { t } = i18n.global
import { formatNumber } from '@/utils/math'
import { getRealmId } from '@/utils/realmId'
import { SENSOR_DATA_ROOM } from '@/interfaces/sensordata.interface'
import { RGBA } from './rgba'
import { ColorScaleConfig, configToMapboxExpression, getColorForValue } from './colorScale'

export type SensorTypes = 'temperature' | 'co2' | 'humidity' | 'flow' | 'presence' | 'booking'

export const UNITS: Record<SensorTypes, string> = {
  temperature: '˚C',
  co2: 'ppm',
  humidity: '%',
  flow: 'l/min', //check with backend! l/s or l/min?
  presence: '',
  booking: ''
}

const BLUE = new RGBA(16, 61, 255, 0.5).toHex()
const GREEN = new RGBA(0, 255, 0, 0.4).toHex()
const RED = new RGBA(255, 99, 71, 0.6).toHex()

const DEFAULT_SENSOR_CONFIG: Record<SensorTypes, ColorScaleConfig> = {
  temperature: {
    type: 'STEP',
    cutoffs: [{ color: BLUE }, { value: 19, color: GREEN }, { value: 22, color: RED }]
  },
  co2: {
    type: 'STEP',
    cutoffs: [{ color: BLUE }, { value: 400, color: GREEN }, { value: 700, color: RED }]
  },
  humidity: {
    type: 'STEP',
    cutoffs: [{ color: BLUE }, { value: 30, color: GREEN }, { value: 40, color: RED }]
  },
  flow: {
    type: 'STEP',
    cutoffs: [{ color: BLUE }, { value: 3, color: GREEN }, { value: 10, color: RED }]
  },
  presence: {
    type: 'STEP',
    cutoffs: [{ color: GREEN }, { value: 1, color: RED }]
  },
  booking: {
    type: 'STEP',
    cutoffs: [{ color: BLUE }, { value: 0, color: GREEN }, { value: 1, color: RED }]
  }
}

const AH_SENSOR_CONFIG: Record<SensorTypes, ColorScaleConfig> = {
  temperature: {
    type: 'STEP',
    cutoffs: [{ color: BLUE }, { value: 21, color: GREEN }, { value: 24, color: RED }]
  },
  co2: {
    type: 'STEP',
    cutoffs: [{ color: GREEN }, { value: 800, color: RED }]
  },
  humidity: {
    type: 'SINGLE',
    cutoffs: [{ color: GREEN }]
  },
  flow: {
    type: 'SINGLE',
    cutoffs: [{ color: GREEN }]
  },
  presence: {
    type: 'STEP',
    cutoffs: [{ color: GREEN }, { value: 1, color: RED }]
  },
  booking: {
    type: 'STEP',
    cutoffs: [{ color: BLUE }, { value: 0, color: GREEN }, { value: 1, color: RED }]
  }
}

export const sensorTypeConfigs =
  getRealmId() === 'akademiskahus' ? AH_SENSOR_CONFIG : DEFAULT_SENSOR_CONFIG

export function getSensorValueText(
  value: number | undefined,
  type: string,
  unit: string
): string | undefined {
  if (value == undefined) return undefined
  if (type === 'temperature') return `${formatNumber(value, 1)} ${unit}`
  else if (type === 'presence') {
    if (value === 0) return t('general__data__registered-not')
    else return t('general__data__registered')
  } else return `${value.toFixed(0)} ${unit}`
}

export function getColorForSensorValue(sensorType: SensorTypes, value: number): string {
  const config = sensorTypeConfigs[sensorType]
  return getColorForValue(config, value).withWhiteBackground().toString()
}

export const collectSensorData = (sensorData: SENSOR_DATA_ROOM, type: string) => {
  const values = sensorData.values
    .filter((sensorValue) => sensorValue.measures === type)
    .map((sensorValue) => sensorValue.value)
  if (!values.length) return
  return values.reduce((partialSum, a) => partialSum + a, 0) / values.length
}

export const getPresence = (sensorData: SENSOR_DATA_ROOM) => {
  const presenceValues = sensorData.values
    .filter((sensorValue) => sensorValue.measures === 'Presence')
    .map((sensorValue) => sensorValue.value)

  if (!presenceValues?.length) return undefined
  if (presenceValues.includes(0)) return 0
  else return 1
}

export function sensorTypeToMapboxExpression(sensorType: SensorTypes) {
  const config = sensorTypeConfigs[sensorType]
  return configToMapboxExpression(sensorType, config)
}
